import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { ScrollButton } from './StreamlineControls';
import classNames from 'classnames';

interface StreamlineProps {
  autoplay?: boolean;
  autoPlayDuration?: number;
  children: ReactElement[] | ReactElement;
  loop?: boolean;
  spacing?: 0 | 16 | 32 | 48;
}

const Streamline = ({
  autoplay = false,
  autoPlayDuration = 5000,
  children,
  loop = false,
  spacing = 16,
}: StreamlineProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const [contentFits, setContentFits] = useState(false);
  const intervalId = useRef<NodeJS.Timer | undefined>(undefined);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (autoplay && intervalId.current === undefined) {
      intervalId.current = setInterval(() => handleScrollClick('right'), autoPlayDuration);
    }
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [autoplay]);

  const handleMobileTouch = useCallback(
    (closingIntervalId?: NodeJS.Timer) => {
      if (autoplay && closingIntervalId !== undefined) {
        clearInterval(closingIntervalId);
        intervalId.current = undefined;
      }
    },
    [autoplay],
  );

  const handleScrollClick = useCallback(
    (direction: 'left' | 'right', closingIntervalId?: NodeJS.Timer) => {
      const left = direction === 'left';
      const scrollWidth = scrollableContainerRef.current?.scrollWidth ?? 0;
      const clientWidth = containerRef.current?.clientWidth ?? 0;
      const scrollLeft = scrollableContainerRef.current?.scrollLeft ?? 0;
      const scrollDistanceBase = clientWidth * 0.92;

      if (autoplay && closingIntervalId !== undefined) {
        clearInterval(closingIntervalId);
        intervalId.current = undefined;
      }

      if (loop && left && scrollLeft <= 5) {
        scrollableContainerRef.current?.scrollBy(scrollWidth, 0);
        return;
      }
      if (loop && !left && scrollLeft >= scrollWidth - clientWidth) {
        scrollableContainerRef.current?.scrollBy(-scrollWidth, 0);
        return;
      }

      scrollableContainerRef.current?.scrollBy(left ? -scrollDistanceBase : scrollDistanceBase, 0);
    },
    [containerRef, scrollableContainerRef, loop, autoplay],
  );

  const handleScroll = useCallback(() => {
    if (!scrollableContainerRef.current) return;
    const scrollLeft = scrollableContainerRef.current?.scrollLeft;
    const scrollWidth = scrollableContainerRef.current?.scrollWidth;
    const containerWidth = containerRef.current?.clientWidth ?? 0;
    const fits = scrollWidth <= containerWidth;
    setContentFits(fits);

    const result = scrollWidth - scrollLeft - containerWidth;
    if (result <= 0) {
      if (!loop) setShowRightScroll(false);
      if (scrollWidth > containerWidth) setShowLeftScroll(true);
      return;
    }
    if (fits) {
      setShowRightScroll(false);
      setShowLeftScroll(false);
      return;
    }

    if (scrollLeft < 20 && containerWidth !== scrollWidth) {
      if (!loop) setShowLeftScroll(false);
      setShowRightScroll(true);
      return;
    }

    setShowLeftScroll(true);
    setShowRightScroll(true);
  }, [loop]);

  useEffect(() => {
    handleScroll();
  }, [children, handleScroll]);

  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (container) {
      container.addEventListener('scrollend', handleScroll);
    }
    return () => {
      container?.removeEventListener('scrollend', handleScroll);
    };
  }, [handleScroll]);

  // Set up ResizeObserver to watch for size changes
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = undefined;
      }
      resizeTimeoutRef.current = setTimeout(() => {
        handleScroll();
      }, 100);
    });

    resizeObserver.observe(container);
    return () => {
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = undefined;
      }
    };
  }, []);

  return (
    <div className="relative flex justify-center items-center" ref={containerRef}>
      <ScrollButton
        direction="left"
        onClick={() => handleScrollClick('left', intervalId.current)}
        show={showLeftScroll}
      />
      <ScrollButton
        direction="right"
        onClick={() => handleScrollClick('right', intervalId.current)}
        show={showRightScroll}
      />
      <div className="w-full flex flex-col overflow-hidden">
        <div
          ref={scrollableContainerRef}
          onTouchStart={() => handleMobileTouch(intervalId.current)}
          className={classNames(
            `w-full h-full flex overflow-x-scroll no-scrollbar`,
            'scroll-smooth snap-x',
            {
              'space-x-[0px]': spacing === 0,
              'space-x-[16px]': spacing === 16,
              'space-x-[32px]': spacing === 32,
              'space-x-[48px]': spacing === 48,
              'justify-center': contentFits,
            },
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Streamline;
